'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/styleUtils';
import { DraggableCar } from '@/modules/captcha/components/CarSprite';
import { PuzzlePrompt, PuzzleScene } from '@/modules/captcha/components/PuzzleFrame';
import { usePuzzleDrag } from '@/modules/captcha/hooks/usePuzzleDrag';
import { BAY_LEFTS, BAY_WIDTH, randomInt } from '@/modules/captcha/shared/puzzle-utils';
import { draggableCar, slotBase, slotCorrect, slotIdle, slotTarget } from './puzzle.style';
import type { PuzzleProps } from './puzzle.types';

const SCENE_HEIGHT = 224;
const HOME = { x: 133, y: 138 };
const BOUNDS = { x: 266, y: 152 };

/** Above this y the car is level with the bays; below it, it is still on the approach lane. */
const BAY_ROW_BOTTOM = 122;

/**
 * Design 1a — drag the car into the highlighted bay.
 *
 * ## Invariants:
 * - The target bay is rerolled per challenge. Why: a fixed target makes the whole puzzle a
 *   single hard-coded drag, which is trivially scriptable and stops being a check at all.
 * - Accepts on bay overlap rather than exact centring. Why: this is a friction gate in front of
 *   a payment, not a dexterity test — a customer on a phone who is plainly in the right bay must
 *   not be told they failed.
 */
export const ParkTheCar = ({ onSolved }: PuzzleProps) => {
  const t = useTranslations('Captcha');
  const [target] = useState(() => randomInt(BAY_LEFTS.length));

  const onDrop = useCallback(
    ({ x, y }: { x: number; y: number }) => {
      const centerX = x + 23;
      const centerY = y + 36;
      if (centerY >= BAY_ROW_BOTTOM) return { accepted: false };

      const hit = BAY_LEFTS.findIndex((left) => centerX >= left && centerX <= left + BAY_WIDTH);
      if (hit !== target) return { accepted: false };

      onSolved();
      return { accepted: true, snapTo: { x: BAY_LEFTS[hit] + 10, y: 26 } };
    },
    [onSolved, target]
  );

  const { position, dragging, shaking, accepted, handlers } = usePuzzleDrag({
    home: HOME,
    bounds: BOUNDS,
    onDrop,
  });

  return (
    <>
      <PuzzlePrompt>{t('parkTheCarPrompt')}</PuzzlePrompt>
      <PuzzleScene height={SCENE_HEIGHT} data-cy="captcha-park-the-car">
        {BAY_LEFTS.map((left, index) => {
          const isTarget = index === target;
          return (
            <div
              key={left}
              className={cn(
                slotBase,
                'tw:top-small tw:flex tw:h-[100px] tw:w-[66px] tw:justify-center',
                !isTarget && slotIdle,
                isTarget && (accepted ? slotCorrect : slotTarget)
              )}
              style={{ left }}
              data-cy={`captcha-bay-${index}`}
            >
              {isTarget && (
                <span
                  className={cn(
                    'tw:mt-nano tw:text-14 tw:font-bold',
                    accepted ? 'tw:text-success' : 'tw:text-primary'
                  )}
                >
                  P
                </span>
              )}
            </div>
          );
        })}
        <div
          {...handlers}
          role="button"
          tabIndex={0}
          aria-label={t('carHandleLabel')}
          className={draggableCar}
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
            transition: dragging ? 'none' : 'transform 0.25s ease',
          }}
          data-cy="captcha-park-the-car-handle"
        >
          <DraggableCar shake={shaking} />
        </div>
      </PuzzleScene>
    </>
  );
};

ParkTheCar.displayName = 'ParkTheCar';
